import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { DataMainTemplate } from "@nocios/presentation-kit";
import Config from "src/Constants/Config";
import setCurrentPage from "src/Utils/setCurrentPage";
import PanelLeft from "src/Component/PanelLeft";

// The BUTTON_CONFIG array index works as the ID.
// The image prefix is derived from the index (e.g., "right_1_", "right_2_", …)
// The number of images in each Right Side Animation is derived from the length of fadeInDuration.
interface ButtonConfig {
  label: string;
  icon: "person-star" | "chart" | "innovation";
  fadeInDuration: number[]; // Array of durations (ms) for each image fade in.
}

const PAGE_ID = 3;
const NEXT_PAGE_ID = PAGE_ID + 1;
const BACK_PAGE_ID = PAGE_ID - 1;
const LAST_PAGE = false;
const BG_ID = 3;
const BG_TYPE = "jpg";

const TITLE = "Leading Digital Solutions";
const DESCRIPTION = "Launch Fiber products on ONE IT stack";

const BUTTON_COLOR = "purple";

const BUTTONS_CONFIG: ButtonConfig[] = [
  {
    label: "About Proximus",
    icon: "person-star",
    fadeInDuration: [50, 300, 300, 300, 300],
  },
  {
    label: "Drivers for Change",
    icon: "chart",
    fadeInDuration: [50, 300, 300, 300, 300],
  },
  {
    label: "Infosys Differentiators",
    icon: "innovation",
    fadeInDuration: [50, 300, 300, 300, 300],
  },
];

// Helper function to generate an array of image URLs.
const createRightImages = (baseImageUrl: string, imagePrefix: string, count: number): string[] =>
  Array.from({ length: count }, (_, i) => `${baseImageUrl}${imagePrefix}${i}.png`);

// Right side animation component.
const RightSideAnimation: React.FC<{
  images: string[];
  active: boolean;
  triggerAnimation: boolean;
  fadeInDurations: number[];
}> = ({ images, active, triggerAnimation, fadeInDurations }) => {
  const [step, setStep] = useState(0);

  // Reset step when a new animation is triggered.
  useEffect(() => {
    if (triggerAnimation) {
      setStep(0);
    }
  }, [triggerAnimation]);

  // Increment the step to reveal images with fade in.
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (triggerAnimation && step < images.length) {
      const duration = fadeInDurations[step] !== undefined ? fadeInDurations[step] : 300;
      timer = setTimeout(() => setStep(step + 1), duration);
    }
    return () => clearTimeout(timer);
  }, [triggerAnimation, step, images.length, fadeInDurations]);

  // Only render if active.
  if (!active) return null;

  const rightImageStyle: React.CSSProperties = {
    position: "absolute",
    right: 0,
    top: 0,
    width: "var(--width-1920)",
    height: "var(--height-1080)",
    transition: "opacity .6s ease-in-out",
  };

  return (
    <div style={{ position: "absolute", top: 0, right: 0 }}>
      {images.map((src, index) => (
        <img
          key={index}
          src={src}
          alt=""
          style={{
            ...rightImageStyle,
            opacity: step > index ? 1 : 0,
          }}
        />
      ))}
    </div>
  );
};

const DATA = (): DataMainTemplate => {
  const currentPage = useSelector((state: { appReducer: { currentPage: number } }) => state.appReducer.currentPage);
  // Initialize state based on the current page to avoid a visible re-render delay.
  const [activeButton, setActiveButton] = useState<number>(0);
  const [triggerAnimation, setTriggerAnimation] = useState<boolean>(false);

  const baseImageBG = `${Config.BASE_URL}/images/BG/`;
  const baseImageUrl = `${Config.BASE_URL}/images/screen_${PAGE_ID}/`;

  const rightImagesMap = BUTTONS_CONFIG.reduce((acc, button, index) => {
    const imagePrefix = `right_${index + 1}_`;
    const maxImages = button.fadeInDuration.length;
    acc[index] = createRightImages(baseImageUrl, imagePrefix, maxImages);
    return acc;
  }, {} as { [key: number]: string[] });

  // Update states if currentPage changes.
  useEffect(() => {
    if (currentPage === PAGE_ID) {
      setTriggerAnimation(true);
    } else {
      setTriggerAnimation(false);
      setActiveButton(0);
    }
  }, [currentPage]);

  const handleButtonClick = (index: number) => {
    setTriggerAnimation(false);
    setTimeout(() => {
      setActiveButton(index);
      setTriggerAnimation(true);
    }, 0);
  };

  const dataBtns = BUTTONS_CONFIG.map((button, index) => ({
    id: index,
    label: button.label,
    icon: button.icon,
    style: {
      cursor: "pointer",
      transition: "background-color 0.3s",
    } as React.CSSProperties,
  }));

  const backButton =
    BACK_PAGE_ID > 0
      ? {
          label: "Back",
          type: "back" as const,
          startIcon: { icon: "arrow-right", color: "#fff" },
          onClick: () => setCurrentPage(BACK_PAGE_ID),
          style: {
            borderRadius: "var(--width-45)",
            padding: "var(--height-5) var(--width-25)",
            gap: "var(--width-16)",
          },
        }
      : undefined;

  const nextButton = !LAST_PAGE
    ? {
        label: "Next",
        type: "next" as const,
        endIcon: { icon: "arrow-left" },
        onClick: () => setCurrentPage(NEXT_PAGE_ID),
        style: {
          borderRadius: "45px",
          padding: "5px 25px",
          gap: "var(--width-16)",
        },
      }
    : undefined;

  return {
    bgVideo: {
      poster: `${baseImageBG}bg_${BG_ID}.${BG_TYPE}`,
      video: `${Config.BASE_URL}/bg_${BG_ID}.mp4`,
    },
    gradient:
      "linear-gradient(270deg, rgba(21, 50, 121, 0.00) -16.3%, rgba(21, 50, 121, 0.80) 100.68%), linear-gradient(0deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.60) 100%)",
    title: TITLE,
    description: DESCRIPTION,
    leftAfterDescription: <PanelLeft dataBtns={dataBtns} active={activeButton} setActive={handleButtonClick} color={BUTTON_COLOR} />,
    rightSideComponent: (
      <>
        {BUTTONS_CONFIG.map((button, index) => (
          <RightSideAnimation
            key={index}
            images={rightImagesMap[index]}
            active={activeButton === index}
            triggerAnimation={triggerAnimation}
            fadeInDurations={button.fadeInDuration}
          />
        ))}
      </>
    ),
    next: nextButton,
    back: backButton,
  };
};

export default DATA;
